import CloseIcon from "@mui/icons-material/Close";
import { Box, IconButton, Skeleton, Typography } from "@mui/material";
import { alpha } from "@mui/material/styles";
import type React from "react";
import type { FC, ReactNode } from "react";
import { ALERT_CORAL, BADGE, FONT_SIZE, INK, LETTER_SPACING, MOTION, NEUTRAL, STAT_TILE, STATUS, SURFACE } from "../../../theme/tokens";
import { CommonDashboardBox } from "../../Dashboard/CommonDashboardBox";
import { AnimatedNumber } from "../Animations/AnimatedNumber";

/**
 * Whether the tile is currently driving a filter, and in which direction.
 *
 * `matching` = the surface below is showing the rows this tile counts; `not-matching` = it is
 * showing the inverse. Two states rather than one boolean because a tile that can invert has to say
 * which way it went — "selected" alone leaves the reader guessing what they are looking at.
 */
export type ClickableStatState = "default" | "matching" | "not-matching";

type ClickableStatTileProps = {
	label: string;
	value: number;
	icon?: ReactNode;
	state?: ClickableStatState;
	/**
	 * Already-translated badge text for the active state (e.g. "Filtering the table"). Omit and no
	 * badge renders — the accent still marks the tile as active.
	 */
	stateLabel?: string;
	onClick?: () => void;
	/**
	 * Renders an ✕ on the active tile. Give it whenever the tile applies a filter: the way out of a
	 * filter should be on the control that applied it, not somewhere else on the page.
	 */
	onClear?: () => void;
	/** Already-translated label for the ✕ (accessibility). */
	clearLabel?: string;
	/**
	 * Skeleton the figure instead of rendering it. Use it rather than showing a provisional number:
	 * a value that changes after it has been read is worse than one that arrives late, and a tile
	 * whose count moves while you are reaching for it is a tile you stop trusting.
	 */
	loading?: boolean;
	dataTestId?: string;
};

const STATE_ACCENT: Record<Exclude<ClickableStatState, "default">, string> = {
	matching: STATUS.SUCCESS.main,
	"not-matching": ALERT_CORAL,
};

/**
 * A KPI tile that doubles as a filter control: a label, a count-up figure, an optional icon, and an
 * active state that says the surface below is currently filtered by it.
 *
 * Compose these in a responsive grid to form a KPI row. Keep the figure ABSOLUTE — a tile whose
 * value follows the filter it applies changes the moment you click it, which leaves nothing stable
 * to click back to and makes the tile read as a number one moment and a button the next.
 */
export const ClickableStatTile: FC<ClickableStatTileProps> = ({
	label,
	value,
	icon,
	state = "default",
	stateLabel,
	onClick,
	onClear,
	clearLabel,
	loading = false,
	dataTestId,
}) => {
	const clickable = !!onClick;
	const isActive = state !== "default";
	const accent = isActive ? STATE_ACCENT[state] : undefined;
	const showClear = isActive && !!onClear;

	return (
		<CommonDashboardBox
			role={clickable ? "button" : undefined}
			tabIndex={onClick ? 0 : -1}
			data-testid={dataTestId}
			onClick={onClick}
			onKeyDown={
				onClick
					? (e: React.KeyboardEvent<HTMLDivElement>) => {
							if (e.key === "Enter" || e.key === " ") {
								e.preventDefault();
								onClick();
							}
						}
					: undefined
			}
			sx={(theme) => ({
				// Override the default `width: fit-content` so tiles grow to fill the responsive grid.
				width: "auto",
				flex: "1 1 0",
				minWidth: STAT_TILE.MIN_WIDTH,
				minHeight: STAT_TILE.MIN_HEIGHT,
				position: "relative",
				display: "flex",
				flexDirection: "column",
				justifyContent: "space-between",
				padding: "10px 14px 10px 18px",
				backgroundColor: isActive && accent ? alpha(accent, 0.06) : SURFACE,
				border: `1px solid ${isActive && accent ? alpha(accent, 0.35) : alpha(theme.palette.primary.main, 0.08)}`,
				cursor: clickable ? "pointer" : "default",
				userSelect: "none",
				transition: `transform ${MOTION.DURATION.CONTROL}ms ${MOTION.EASE_OUT}, background-color ${MOTION.DURATION.OVERLAY_FADE}ms ${MOTION.EASE_OUT}, border-color ${MOTION.DURATION.OVERLAY_FADE}ms ${MOTION.EASE_OUT}, box-shadow ${MOTION.DURATION.OVERLAY_FADE}ms ${MOTION.EASE_OUT}`,
				"@media (prefers-reduced-motion: reduce)": { transition: "none" },
				outline: "none",
				"&::before": {
					content: '""',
					position: "absolute",
					top: 8,
					bottom: 8,
					left: 0,
					width: STAT_TILE.ACCENT_RAIL_WIDTH,
					borderRadius: `0 ${STAT_TILE.ACCENT_RAIL_WIDTH}px ${STAT_TILE.ACCENT_RAIL_WIDTH}px 0`,
					backgroundColor: accent ?? "transparent",
					transition: `background-color ${MOTION.DURATION.OVERLAY_FADE}ms ${MOTION.EASE_OUT}`,
				},
				"@media (hover: hover) and (pointer: fine)": {
					"&:hover": clickable
						? {
								borderColor: isActive && accent ? alpha(accent, 0.55) : alpha(theme.palette.primary.main, 0.24),
								boxShadow: `0px 2px 8px 0px ${alpha(theme.palette.primary.main, 0.1)}, 0px 1px 3px 0px ${alpha(NEUTRAL[600], 0.06)}`,
								transform: "translateY(-1px)",
							}
						: undefined,
				},
				"&:active": clickable ? { transform: "scale(0.97)" } : undefined,
				"&:focus-visible": clickable
					? {
							borderColor: theme.palette.primary.main,
							boxShadow: `0 0 0 3px ${alpha(theme.palette.primary.main, 0.16)}`,
						}
					: undefined,
			})}
		>
			<Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 1 }}>
				<Typography
					sx={{
						fontSize: FONT_SIZE.LABEL_XS,
						fontWeight: 600,
						color: INK.MUTED,
						lineHeight: 1.2,
						textTransform: "uppercase",
						letterSpacing: LETTER_SPACING.LABEL,
					}}
				>
					{label}
				</Typography>
				{showClear ? (
					<IconButton
						size="small"
						aria-label={clearLabel}
						onClick={(event) => {
							// The tile itself is a button; without this the clear would immediately re-apply it.
							event.stopPropagation();
							onClear();
						}}
						data-testid={dataTestId ? `${dataTestId}-clear` : undefined}
						sx={{ p: 0.25, color: accent }}
					>
						<CloseIcon sx={{ fontSize: 14 }} />
					</IconButton>
				) : (
					icon && <Box sx={{ display: "flex", color: INK.MUTED, lineHeight: 0 }}>{icon}</Box>
				)}
			</Box>

			<Box sx={{ display: "flex", alignItems: "baseline", justifyContent: "space-between", gap: 1, mt: 0.5 }}>
				{loading ? (
					<Skeleton variant="text" sx={{ width: 56, fontSize: FONT_SIZE.STAT_VALUE }} data-testid={dataTestId ? `${dataTestId}-skeleton` : undefined} />
				) : (
					<Typography
						sx={{
							fontSize: FONT_SIZE.STAT_VALUE,
							fontWeight: 700,
							color: INK.PRIMARY,
							lineHeight: 1,
							fontVariantNumeric: "tabular-nums",
							letterSpacing: "-0.02em",
						}}
					>
						<AnimatedNumber value={value} />
					</Typography>
				)}
				{isActive && stateLabel && (
					<Box
						sx={{
							px: 0.875,
							py: 0.25,
							borderRadius: BADGE.PILL_RADIUS_FULL,
							backgroundColor: accent,
							color: SURFACE,
							fontSize: FONT_SIZE.LABEL_MICRO,
							fontWeight: 700,
							lineHeight: 1.4,
							textTransform: "uppercase",
							letterSpacing: LETTER_SPACING.LABEL,
							whiteSpace: "nowrap",
						}}
						data-testid={dataTestId ? `${dataTestId}-state-badge` : undefined}
					>
						{stateLabel}
					</Box>
				)}
			</Box>
		</CommonDashboardBox>
	);
};
